// src/components/PurchaseFlow/Step/ConfirmationStep.js
import React, { useState, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateAvailableCredit as updateCreditUtils } from '../../../utils/creditUtils';
import { formatCurrency, formatNumber } from '../../../utils/numberFormatter';
import DefaultConfirmationView from './ConfirmationViews/DefaultConfirmationView';
import Variant1ConfirmationView from './ConfirmationViews/Variant1ConfirmationView';
import DefaultSuccessView from './ConfirmationViews/DefaultSuccessView';
import Variant1SuccessView from './ConfirmationViews/Variant1SuccessView';
import { 
  faCheck, 
  faPrint, 
  faDownload, 
  faHome, 
  faFileAlt,
  faFileSignature,
  faInfoCircle,
  faFlagCheckered,
  faClock,
  faCalendar,
  faListCheck,
  faHandHoldingUsd,
  faCalendarCheck,
  faExclamationTriangle,
  faDollarSign,
  faCalculator,
  faPercentage,
  faCheckCircle,
  faUser,
  faCashRegister,
  faReceipt,
  faFileContract
} from '@fortawesome/free-solid-svg-icons';
import { SelectedOfferContext } from '../../../context/SelectedOfferContext';
import { ClientContext } from '../../../context/ClientContext';
import { useProgress } from '../../../context/ProgressContext';
import api from '../../../services/api';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import SaleDetailModal from '../../Sales/SaleDetailModal';
import PaymentModal from '../Steps/Payment/PaymentModal';
import { PdfViewerModal } from '../../Common';
import './ConfirmationStep.css';
import QRCode from 'qrcode';

const MySwal = withReactContent(Swal);

// Utilidades para leer textos configurables desde .env
const getEnvText = (envVar, defaultText) => {
  const value = process.env[envVar];
  return value && value.trim() !== '' ? value : defaultText;
};

// Normaliza los saltos de línea para soportar \n y \r\n
const normalizeNewlines = (text) => (text || '').replace(/\\n/g, '\n').replace(/\r\n/g, '\n').replace(/\n/g, '\n');

const paymentMethodsText = normalizeNewlines(getEnvText(
  'REACT_APP_CONFIRMATION_STEP_PAYMENT_METHODS_TEXT',
  '- Débito automático en tarjeta o cuenta bancaria.\n- Débito interno o DEBIN Spot si está habilitado.\n- Transferencia bancaria o pago en el domicilio de la financiera.'
));
const earlyPaymentText = normalizeNewlines(getEnvText(
  'REACT_APP_CONFIRMATION_STEP_EARLY_PAYMENT_TEXT',
  'Puede cancelar anticipadamente sin penalización.'
));
const latePenaltyText = normalizeNewlines(getEnvText(
  'REACT_APP_CONFIRMATION_STEP_LATE_PENALTY_TEXT',
  'Interés punitorio del 50% sobre la tasa vigente.'
));

const ConfirmationStep = ({ onNext, purchaseRequestId, clientData }) => {
  const { selectedOffer } = useContext(SelectedOfferContext);
  const { setClientData } = useContext(ClientContext);
  const { setProgressText, reloadProgress } = useProgress();
  const [isProcessing, setIsProcessing] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contractDownloaded, setContractDownloaded] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [saleId, setSaleId] = useState(null);
  const [customerMyAccount, setCustomerMyAccount] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [payment, setPayment] = useState(null);

  // Estados para el modal del PDF
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfTitle, setPdfTitle] = useState('');

  const updateAvailableCredit = async () => {
    try {
      const response = await api.get(`/customers/${clientData.id}/credit-info`);
      
      if (response.data.status === 'COMPLETED') {
        setClientData(prevData => ({
          ...prevData,
          total_limit: response.data.limits.total_limit,
          available_credit: response.data.limits.available_limit,
          status: 'COMPLETED'
        }));
        setProgressText('Disponible');
      } else if (response.data.status === 'REJECTED') {
        setClientData(prevData => ({
          ...prevData,
          total_limit: 0,
          available_credit: 0,
          status: 'REJECTED',
          message: response.data.message
        }));
      }
    } catch (error) {
      console.error('Error al actualizar límite de crédito:', error);
    }
  };

  const handleConfirmWithPayment = (paymentInfo) => {
    handleConfirmation(paymentInfo);
  };

  const handleConfirmation = async (paymentData = null) => {
    setIsProcessing(true);
    try {
      const formData = new FormData();
      
      formData.append('purchase_request_id', purchaseRequestId);

      // Incluir documentos pendientes para carga diferida
      const allowDeferredDocumentation = process.env.REACT_APP_ALLOW_DEFERED_DOCUMENTATION === 'true';
      if (allowDeferredDocumentation) {
        const requiredDocs = process.env.REACT_APP_FLOW_NEW_REQUIRED_DOCS;
        if (requiredDocs && requiredDocs.trim()) {
          try {
            const pendingDocuments = requiredDocs
              .split(',')
              .map(doc => doc.trim())
              .filter(doc => doc.length > 0);
            
            if (pendingDocuments.length > 0) {
              formData.append('pendingDocuments', JSON.stringify(pendingDocuments));
            }
          } catch (error) {
            console.warn('Error procesando documentos pendientes:', error);
            // No enviamos pendingDocuments si hay error, pero continuamos con el flujo
          }
        }
      }

      if (paymentData) {
        formData.append('requiresAdvance', 'true');
        formData.append('payment_method', paymentData.payment_method);
        formData.append('amount', selectedOffer?.downpaymentAmount || null);

        formData.append('downpayment_type', selectedOffer?.downpaymentType || null);
        formData.append('downpayment_value', selectedOffer?.downpaymentValue || null);
        
        if (paymentData.payment_method === 'bank_transfer' && paymentData.voucher) {
          formData.append('voucher', paymentData.voucher);
        }
      }

      const response = await api.post(
        `/customers/${clientData.id}/purchase-requests/${purchaseRequestId}/confirm`,
        formData
      );
      
      setContracts(response.data.contracts);
      setSaleId(response.data.sale_id);
      setCustomerMyAccount(response.data.customer);
      if (response.data.payment) {
        setPayment(response.data.payment);
      }

      updateAvailableCredit();

      onNext(null, 'success');
    } catch (error) {
      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Hubo un error al confirmar la operación.',
      });
      console.error('Error confirming purchase:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePrintContract = (url) => {
    setPdfUrl(url);
    setPdfTitle('Vista Previa del Contrato');
    setShowPdfModal(true);
    setContractDownloaded(true);
  };

  const handlePrintReceipt = (url) => {
    setPdfUrl(url);
    setPdfTitle('Vista Previa del Comprobante');
    setShowPdfModal(true);
  };

  const handleViewDetail = () => {
    setSelectedSale({ id: saleId, customer: { id: clientData.id } });
    setShowDetailModal(true);
  };

  const handleShareMyAccount = () => {
    if (!customerMyAccount) return;

    const clientName = clientData.firstName || '';

    const whatsappText = encodeURIComponent(
      `Hola ${clientName}, aquí puedes ver toda la información de tus préstamos y realizar gestiones: ${customerMyAccount.my_account_url}`
    );
    
    const phone = customerMyAccount.phone?.[0]?.applicant_phone?.phone;
    const whatsappUrl = phone ? `https://wa.me/${phone}?text=${whatsappText}` : `https://wa.me/?text=${whatsappText}`;

    showQRModal(
      customerMyAccount.my_account_url,
      'Portal Mi Cuenta',
      'El cliente puede escanear este código QR para acceder a "Mi Cuenta", para realizar gestiones y consultas.',
      whatsappUrl
    );
  };

  const showQRModal = async (url, title, description, whatsappUrl = null) => {
    try {
      const canvas = document.createElement('canvas');
      await QRCode.toCanvas(canvas, url, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });

      const qrDataUrl = canvas.toDataURL();

      MySwal.fire({
        title,
        html: `
          <div class="text-center">
            <img src="${qrDataUrl}" class="mb-4" alt="QR Code" />
            <p class="mb-3">${description}</p>
              <hr />
              <div class="row g-3">
                <div class="col-${whatsappUrl ? '6' : '12'}">
                  <p class="text-muted small mb-2">${whatsappUrl ? 'Copia este enlace:' : 'O copia este enlace para compartir con el cliente:'}</p>
                  <div class="input-group">
                    <input 
                      type="text" 
                      value="${url}" 
                      class="form-control form-control-sm"
                      readonly
                    />
                    <button 
                      class="btn btn-outline-secondary copy-button"
                      type="button"
                      style="border: 1px solid #dce7f1 !important"
                      onclick="copyToClipboard('${url}', this)"
                    >
                      <i class="bi bi-clipboard"></i>
                    </button>
                  </div>
                </div>
                ${whatsappUrl ? `
                  <div class="col-6">
                    <p class="text-muted small mb-2">O comparte con el cliente:</p>
                    <a 
                      href="${whatsappUrl}" 
                      target="_blank" 
                      class="btn btn-success w-100"
                      style="background-color: #25D366; border-color: #25D366"
                    >
                      <i class="bi bi-whatsapp me-2"></i>
                      Enviar por WhatsApp
                    </a>
                  </div>
                ` : ''}
              </div>
          </div>
        `,
        showConfirmButton: false,
        showCloseButton: true,
        didOpen: () => {
          window.copyToClipboard = (text, button) => {
            navigator.clipboard.writeText(text).then(() => {
              const originalContent = button.innerHTML;
              button.innerHTML = '<i class="bi bi-check"></i>';
              button.disabled = true;
              setTimeout(() => {
                button.innerHTML = originalContent;
                button.disabled = false;
              }, 2000);
            });
          };
        }
      });
    } catch (error) {
      console.error('Error generando QR:', error);
    }
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
  };

  const handleConfirmButtonClick = () => {
    // Si requiere pago inicial, mostrar el modal
    if (selectedOffer?.isDownpaymentPlan) {
      setShowPaymentModal(true);
    } else {
      // Si no requiere pago inicial, confirmar directamente
      handleConfirmation();
    }
  };

  // Función para seleccionar el componente de vista de confirmación
  const getConfirmationViewComponent = (viewType) => {
    switch (viewType) {
      case 'variant1':
        return Variant1ConfirmationView;
      case 'default':
      default:
        return DefaultConfirmationView;
    }
  };

  // Función para seleccionar el componente de vista de éxito
  const getSuccessViewComponent = (viewType) => {
    switch (viewType) {
      case 'variant1':
        return Variant1SuccessView;
      case 'default':
      default:
        return DefaultSuccessView;
    }
  };

  // Función simple para elegir vista basada solo en ENV
  const renderConfirmationView = () => {
    // Determinar tipo de vista desde ENV o usar default
    const viewType = process.env.REACT_APP_CONFIRMATION_VIEW || 'default';
    
    // Obtener componente usando switch
    const ViewComponent = getConfirmationViewComponent(viewType);

    // Log para debugging (solo en desarrollo)
    if (process.env.NODE_ENV === 'development') {
      console.log(`[ConfirmationStep] Vista: ${ViewComponent.name} (env: ${viewType})`);
    }

    return (
      <>
        <ViewComponent
          selectedOffer={selectedOffer}
          isProcessing={isProcessing}
          onConfirmButtonClick={handleConfirmButtonClick}
          clientData={clientData}
        />
        
        {/* Payment Modal - común para todas las vistas */}
        <PaymentModal
          show={showPaymentModal}
          onClose={handleClosePaymentModal}
          onConfirm={handleConfirmWithPayment}
          selectedOffer={selectedOffer}
          isProcessing={isProcessing}
        />
      </>
    );
  };

  // Función para renderizar la vista de éxito
  const renderSuccessView = () => {
    // Determinar tipo de vista desde ENV o usar default
    const viewType = process.env.REACT_APP_CONFIRMATION_VIEW || 'default';
    
    // Obtener componente usando switch
    const SuccessComponent = getSuccessViewComponent(viewType);

    // Log para debugging (solo en desarrollo)
    if (process.env.NODE_ENV === 'development') {
      console.log(`[ConfirmationStep] Vista de éxito: ${SuccessComponent.name} (env: ${viewType})`);
    }

    return (
      <SuccessComponent
        contracts={contracts}
        payment={payment}
        customerMyAccount={customerMyAccount}
        contractDownloaded={contractDownloaded}
        onPrintContract={handlePrintContract}
        onPrintReceipt={handlePrintReceipt}
        onShareMyAccount={handleShareMyAccount}
        onViewDetail={handleViewDetail}
      />
    );
  };

  if (contracts.length === 0) {
    return renderConfirmationView();
  }

  return (
    <>
      {renderSuccessView()}
      {showDetailModal && (
        <SaleDetailModal
          saleId={selectedSale.id}
          customerId={selectedSale.customer.id}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedSale(null);
          }}
        />
      )}
      {showPdfModal && (
        <PdfViewerModal
          show={showPdfModal}
          onHide={() => setShowPdfModal(false)}
          url={pdfUrl}
          title={pdfTitle}
        />
      )}
    </>
  );
};

export default ConfirmationStep;
